import React from 'react';
import { cn } from '@/lib/utils';

type Status = 'on' | 'off' | 'alarm';

interface StatusBadgeProps {
  status: Status | string | undefined;
  className?: string;
  isAlarm: boolean
}

const statusConfig = {
  on: {
    color: 'bg-[#15B8B4]',
    textColor: 'text-black'
  },
  off: {
    color: 'bg-[#B4B4B4]',
    textColor: 'text-black'
  },
  'False': { // Handle 'False' string from API
    color: 'bg-[#15B8B4]',
    textColor: 'text-black'
  },
  'True': { // Handle 'True' string from API
    color: 'bg-[#B4B4B4]',
    textColor: 'text-black'
  },
  alarm: {
    color: 'bg-[#EA663F]',
    textColor: 'text-[#EF4337]'
  },
  undefined: {
    color: 'bg-gray-300',
    textColor: 'text-gray-500'
  },
  nodata: {
    color: 'bg-[#757575]',
    textColor: 'text-gray-500'
  }
} as const;

// Map API values to UI friendly values
const normalizeStatus = (status: string | undefined): Status | 'undefined' | 'nodata' => {
  if (status === undefined) return 'nodata';

  // Convert to lowercase for case-insensitive comparison
  const statusLower = status.toLowerCase();

  // Handle various forms of 'on' status
  if (statusLower === 'on' || statusLower === 'true' || status === '1') return 'on';

  // Handle various forms of 'off' status
  if (statusLower === 'off' || statusLower === 'false' || status === '0') return 'off';

  return status as Status;
};

export function StatusBadge({ status, isAlarm, className }: StatusBadgeProps) {
  // Normalize status to handle various formats
  const normalizedStatus = normalizeStatus(status);

  // Choose the right config based on status
  let configMap = normalizedStatus as keyof typeof statusConfig;
  if (isAlarm) {
    configMap = 'alarm';
  }

  // Fallback to 'off' if no matching config found
  const config = statusConfig[configMap] || statusConfig.nodata;

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <span className={cn("h-2 w-2 rounded-full", config.color)} />
      <span className={cn("paragraph-12 capitalize font-inter", config.textColor)} style={{ fontFamily: 'Inter, sans-serif !important' }}>
        {normalizedStatus === 'nodata' ? 'N/A' : normalizedStatus}
      </span>
    </div>
  );
}